import { useCallback } from 'react';
import { useTranslation } from '@aimsos/i18n';

import { validateApiResponse } from '../../../services';

/**
 * The change flow shared by the member tabs: send the request, refetch the tab, and toast. Returns
 * `{ mutate, notifyRefreshFailed }`. `mutate(request)` resolves `true` when the request worked
 * (even if the refetch failed, which only raises the "list may be out of date" toast) and `false`
 * after toasting the failure.
 *
 * @param {object} options
 * @param {() => Promise<boolean|null>} options.refresh `refresh` of `useMemberTabData`
 * @param {string} options.failedTitleKey i18n key of the failure toast title
 * @param {{ current: object }} options.toastRef see `useToastRef`
 */
export const useMemberTabMutation = ({ refresh, failedTitleKey, toastRef }) => {
  const { t } = useTranslation('people-access');

  const notifyRefreshFailed = useCallback(
    () =>
      toastRef.current.error({
        title: t('memberTabs.refreshFailed.title'),
        message: t('memberTabs.refreshFailed.message'),
      }),
    [toastRef, t],
  );

  const mutate = useCallback(
    async (request) => {
      try {
        validateApiResponse(await request(), t(failedTitleKey));
        if ((await refresh()) === false) notifyRefreshFailed();
        return true;
      } catch (err) {
        toastRef.current.error({
          title: t(failedTitleKey),
          message: err?.message ?? t('memberDetail.toast.unknown'),
        });
        return false;
      }
    },
    [refresh, failedTitleKey, notifyRefreshFailed, toastRef, t],
  );

  return { mutate, notifyRefreshFailed };
};
